import React, { FC, useEffect, useState, useCallback, Fragment } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { App, Button, Flex, notification, Spin, Modal, Input, Space, Typography, Tooltip, Image, Empty } from "antd";
import type { UploadFile, UploadProps } from "antd";
import { RcFile } from "antd/es/upload";
import {
  DownloadOutlined,
  ExclamationCircleFilled,
  UploadOutlined,
  FolderAddOutlined,
  ArrowLeftOutlined,
  DeleteOutlined,
  CameraOutlined,
} from "@ant-design/icons";

import dayjs from "dayjs";
import IconFolder from "@/assets/images/fileIcon/folder.svg";

import { Doc, DocTypeEnum } from "@/types/doc";
import { User } from "@/types/user";

import { docAdd, docDisable, docFetch, updatePid } from "@/apis/doc";
import { getFileUrl } from "@/apis/file";

import { RootState } from "@/store";
import { upload } from "@/utils/file/upload";
import { downloadFile, getFileIcon, handlePreviewFile, image } from "./utils";
import { DocWrap } from "./style";
import { getUserName } from "@/utils/user";
import MoveModal from "./components/MoveModal";
import TakePhoto from "@/components/TakePhoto";
import fileMove from "@/assets/images/common/file-move.svg";

export interface SDocProps {
  docId?: string;
  doc?: Doc;
  onChangeDocId?: (id: string) => void;
  accept?: string;
  isRead?: boolean;
  handleNewDocId?: (newId: string) => void;
  forceReset?: boolean;
}

const SDoc: FC<SDocProps> = ({ docId: propDocId, doc, onChangeDocId, accept, isRead = false, handleNewDocId, forceReset = false }) => {
  const { t } = useTranslation();
  const { modal } = App.useApp();
  const currentUser: User = useSelector(
    (state: RootState) => state.userReducer.curUser
  );

  const [docId, setDocId] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [fileList, setFileList] = useState<Array<UploadFile<Doc>>>([]);
  const [folderModalVisible, setFolderModalVisible] = useState<boolean>(false);
  const [cameraOpen, setCameraOpen] = useState<boolean>(false);
  const [folderName, setFolderName] = useState<string>("");
  const [currentPath, setCurrentPath] = useState<string>("/");
  const [rootDoc, setRootDoc] = useState<Doc | null>(null); // 唯一数据源
  // 路径栈，只保存路径信息用于导航
  const [pathStack, setPathStack] = useState<Array<{
    path: string,
    folderId: string,
    folderName: string
  }>>([]);
  const [currentFolderId, setCurrentFolderId] = useState<string>(""); // 当前文件夹ID
  const [isNavigating, setIsNavigating] = useState<boolean>(false); // 防止重复导航
  const [isCreatingFolder, setIsCreatingFolder] = useState<boolean>(false); // 创建文件夹loading状态
  const [isUploading, setIsUploading] = useState<boolean>(false); // 上传文件状态
  const [isMoving, setIsMoving] = useState<boolean>(false); // 移动文件状态
  const [draggingId, setDraggingId] = useState<string | null>(null); // 正在拖拽的项ID
  const [dropTargetId, setDropTargetId] = useState<string | null>(null); // 悬停的目标文件夹ID
  const [dropOnBreadcrumb, setDropOnBreadcrumb] = useState<boolean>(false); // 是否悬停在目录索引区
  const [moveModalVisible, setMoveModalVisible] = useState<boolean>(false); // 移动弹窗状态
  const [selectedDoc, setSelectedDoc] = useState<Doc | null>(null); // 选中的文档
  const [searchKeyword, setSearchKeyword] = useState<string>(""); // 搜索关键字

  useEffect(() => {
    if (propDocId !== docId && propDocId) {
      setLoading(true);
      docFetch(propDocId)
        .then((doc) => {
          setDocId(doc.id);
          setRootDoc(doc); // 保存根文档作为唯一数据源
          setCurrentFolderId(doc.id); // 设置当前文件夹为根目录
          setCurrentPath("/");
          setPathStack([]); // 清空路径栈
        })
        .catch(err => console.log(err))
        .finally(() => {
          setLoading(false);
        })
    }
    if (!!!propDocId && forceReset) {   //如果propDocId为空且需要强制更新为空文件夹
      setDocId("")
      setRootDoc(null)
      setCurrentFolderId("")
      setCurrentPath("/")
      setPathStack([])
    }
  }, [propDocId, docId, forceReset]);

  // 从 rootDoc 中查找指定 folderId 的文件夹
  const findFolderInTree = useCallback((node: Doc, folderId: string): Doc | null => {
    if (node.id === folderId) return node;
    if (node.subs) {
      for (const sub of node.subs) {
        const found = findFolderInTree(sub, folderId);
        if (found) return found;
      }
    }
    return null;
  }, []);

  // 递归搜索匹配关键字的文档和文件夹（排除根节点本身）
  const searchInTree = useCallback((node: Doc, keyword: string, results: Doc[] = [], isRoot = true): Doc[] => {
    if (!keyword) return results;
    const lowerKeyword = keyword.toLowerCase();

    // 不检查根节点本身，只检查子节点
    if (!isRoot && node.name?.toLowerCase().includes(lowerKeyword)) {
      results.push(node);
    }

    // 递归搜索子节点
    if (node.subs) {
      for (const sub of node.subs) {
        searchInTree(sub, keyword, results, false);
      }
    }

    return results;
  }, []);

  // 根据 currentFolderId 从 rootDoc 动态计算 fileList
  useEffect(() => {
    if (!rootDoc || !currentFolderId) {
      setFileList([]);
      return;
    }

    // 如果有搜索关键字，执行搜索
    if (searchKeyword.trim()) {
      const searchResults = searchInTree(rootDoc, searchKeyword.trim());
      setFileList(
        searchResults.map((doc) => ({
          uid: doc.id,
          name: doc.name,
          response: doc,
          status: "done",
          url: getFileUrl(doc?.fileId),
          type: doc.nodeType === DocTypeEnum.NODE ? 'folder' : 'file'
        })) || []
      );
      return;
    }

    // 无搜索关键字时，显示当前文件夹内容
    const currentFolder = findFolderInTree(rootDoc, currentFolderId);
    if (currentFolder) {
      setFileList(
        currentFolder.subs?.map((sub) => ({
          uid: sub.id,
          name: sub.name,
          response: sub,
          status: "done",
          url: getFileUrl(sub?.fileId),
          type: sub.nodeType === DocTypeEnum.NODE ? 'folder' : 'file'
        })) || []
      );
    } else {
      setFileList([]);
    }
  }, [rootDoc, currentFolderId, searchKeyword, findFolderInTree, searchInTree]);

  const handleChange: UploadProps["onChange"] = (info) => {
    setFileList([...info.fileList]);

    // 监控上传状态
    const hasUploading = info.fileList.some(file => file.status === 'uploading');
    setIsUploading(hasUploading);
  };

  const customRequest: UploadProps["customRequest"] = async (options) => {
    if (!docId) {
      try {
        const docRes = await docAdd({
          name: dayjs().unix().toString(),
          nodeType: DocTypeEnum.NODE,
        });
        setDocId(docRes.id);
        handleNewDocId && handleNewDocId(docRes.id)
        uploadDoc(docRes.id, options.file as RcFile);
      } catch (e) { }
    } else {
      uploadDoc(docId, options.file as RcFile);
    }
  };

  const uploadDoc = async (pId: string, file: RcFile) => {
    setIsUploading(true);
    try {
      const uploadedFile = await upload({ name: file.name, size: file.size, file });

      // 使用当前文件夹ID作为父级ID
      const actualParentId = currentFolderId || pId;

      const res = await docAdd({
        pId: actualParentId,
        name: uploadedFile?.name ?? file.name,
        nodeType: DocTypeEnum.LEAF,
        fileId: uploadedFile?.id,
        suffix: uploadedFile?.suffix,
      });

      // 更新 rootDoc（添加到对应文件夹的 subs）
      if (rootDoc) {
        const updateDocTree = (doc: Doc): Doc => {
          if (doc.id === actualParentId) {
            return {
              ...doc,
              subs: [...(doc.subs || []), { ...res, creator: currentUser }]
            };
          }
          if (doc.subs) {
            return {
              ...doc,
              subs: doc.subs.map(updateDocTree)
            };
          }
          return doc;
        };
        setRootDoc(updateDocTree(rootDoc));
      }

      // 更新 fileList 中正在上传的项
      setFileList((prevList) =>
        prevList.map((fileItem) =>
          fileItem.uid === file.uid
            ? {
              uid: res.id,
              name: res.name,
              status: "done" as const,
              url: getFileUrl(uploadedFile?.id ?? ""),
              response: { ...res, creator: currentUser },
            }
            : fileItem
        )
      );

      notification.success({ placement: 'top', message: t('layout.upSucc') });
    } catch (error) {
      // 处理错误情况
      setFileList((prevList) =>
        prevList.map((fileItem) =>
          fileItem.uid === file.uid
            ? { ...fileItem, status: "error" }
            : fileItem
        )
      );
      notification.error({ placement: 'top', message: t('layout.upFail') });
    } finally {
      setIsUploading(false);
    }
  };

  const isImgDoc = (file: UploadFile<Doc>) => {
    let res: boolean = false;
    let nameArr = file.name?.toLowerCase()?.split('.');
    let suffix = nameArr[nameArr.length - 1];
    res = image.includes(suffix);
    return res;
  }

  const props: UploadProps<Doc> = {
    fileList,
    customRequest,
    onChange: handleChange,
    multiple: true,
    showUploadList: {
      extra: ({ response }: UploadFile<Doc>) => {
        return (
          <div>
            {response?.creator && <span>{getUserName(response.creator)}</span>}
            {/* <span style={{ color: "#cccccc" }}>
              ({(response?.size / 1024).toFixed(2)}KB)
            </span> */}
          </div>
        );
      },
      showDownloadIcon: true,
      downloadIcon: (file: UploadFile<Doc>) => {
        // 文件夹不显示下载按钮
        if (file.response?.nodeType === DocTypeEnum.NODE) {
          return <span />;
        }
        return <DownloadOutlined />;
      },
      showRemoveIcon: !isRead,
      removeIcon: (
        <DeleteOutlined style={{ color: "red" }} onClick={e => e.stopPropagation()} />
      )
    },
    onRemove: async (file) => {
      modal.confirm({
        title: <span dangerouslySetInnerHTML={{ __html: t("common.rmTip") }} />,
        icon: <ExclamationCircleFilled />,
        content: (
          <span
            dangerouslySetInnerHTML={{ __html: t("components.rmDocTip") }}
          />
        ),
        onOk: () =>
          docDisable(file.response?.id ?? "")
            .then(() => {
              // 从 rootDoc 中移除被删除的文档
              if (rootDoc && file.response?.id) {
                const removeDocFromTree = (doc: Doc): Doc => {
                  if (doc.subs?.some(sub => sub.id === file.response?.id)) {
                    return {
                      ...doc,
                      subs: doc.subs.filter(sub => sub.id !== file.response?.id)
                    };
                  }
                  if (doc.subs) {
                    return {
                      ...doc,
                      subs: doc.subs.map(removeDocFromTree)
                    };
                  }
                  return doc;
                };
                setRootDoc(removeDocFromTree(rootDoc));
              }
              notification.success({ placement: 'top', message: t('common.rmSuccess') });
            })
            .catch(() => {
              notification.error({ placement: 'top', message: t('common.rmError') });
            }),
      });
      return false;
    },
    onDownload: (file) => {
      if (file.response?.nodeType === DocTypeEnum.LEAF) {
        downloadFile(file.response?.fileId ?? '');
      }
    },
    listType: "picture",
    iconRender: (file) => {
      if (file.response?.nodeType === DocTypeEnum.NODE) {
        return <img draggable={false} src={IconFolder} alt={file.response?.name} style={{ cursor: 'pointer' }} />;
      }
      if (isImgDoc(file)) return <Image src={getFileUrl(file?.response?.fileId ?? '')} preview={{ mask: null }} />
      const fileIconUrl = getFileIcon(file.name, file?.response?.nodeType);
      return <img draggable={false} src={fileIconUrl} alt={file.response?.name} style={{ cursor: 'pointer' }} />;
    },
    onPreview: (file) => {
      if (isImgDoc(file)) return;
      if (file.response?.nodeType === DocTypeEnum.NODE) {
        handleEnterFolder(file.response as Doc);
      } else if (file.response?.nodeType === DocTypeEnum.LEAF && file.response?.fileId) {
        // 文件预览 - 在新窗口打开
        handlePreviewFile(file.response);
      }
    },
    // 自定义列表项渲染，支持拖拽
    itemRender: (originNode, file, _fileList, _actions) => {
      const doc = file.response;
      const isDragging = draggingId === file.uid;
      const isDropTarget = dropTargetId === file.uid;
      const isFolder = doc?.nodeType === DocTypeEnum.NODE;

      // 克隆 originNode 并添加移动按钮
      const enhancedNode = React.cloneElement(originNode as React.ReactElement, {
        children: (
          <>
            {originNode.props.children}
            {!isRead && (
              <Tooltip title={t('components.move')}>
                <img src={fileMove}
                  style={{ marginLeft: 8, marginTop: 0, cursor: 'pointer', width: 15, height: 15 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedDoc(doc || null);
                    setMoveModalVisible(true);
                  }}
                />
              </Tooltip>
            )}
          </>
        )
      });

      return (
        <div
          key={file.uid}
          draggable={!isRead && !isMoving}
          onDragStart={(e) => {
            handleDragStart(e, file.uid);
            e.dataTransfer.setData('text/plain', file.uid);
          }}
          onDragEnd={() => {
            setDraggingId(null);
            setDropTargetId(null);
          }}
          onDragOver={(e) => {
            if (isFolder && !isRead && !isMoving) {
              handleDragOverFolder(e, file.uid);
            }
          }}
          onDragLeave={(e) => {
            handleDragLeaveFolder(e);
          }}
          onDrop={(e) => {
            if (isFolder && !isRead && !isMoving) {
              handleDropOnFolder(e, file.uid);
            }
          }}
          style={{
            opacity: isDragging ? 0.5 : 1,
            cursor: !isRead && !isMoving ? 'grab' : 'default',
            border: isDropTarget && isFolder ? '1px dashed #1890ff' : '1px solid transparent',
            borderRadius: isDropTarget && isFolder ? '4px' : '0',
            backgroundColor: isDropTarget && isFolder ? '#e6f7ff' : 'transparent',
            padding: isDropTarget && isFolder ? '4px' : '0',
            transition: 'all 0.2s',
          }}
          onClick={(e) => {
            if (isImgDoc(file)) return;
            // 点击事件：文件夹进入，文件预览
            if (doc?.nodeType === DocTypeEnum.NODE) {
              handleEnterFolder(doc);
            } else if (doc?.nodeType === DocTypeEnum.LEAF && doc?.fileId) {
              handlePreviewFile(doc);
            }
          }}
        >
          {enhancedNode}
        </div>
      );
    },
  };

  const handleEnterFolder = async (folder: Doc) => {
    // 防止重复点击
    if (isNavigating) return;

    setIsNavigating(true);

    try {
      // 从 rootDoc 中获取最新的文件夹数据
      const currentFolder = findFolderInTree(rootDoc!, folder.id) || folder;

      // 如果在搜索模式下，需要重新构建完整路径
      if (searchKeyword.trim()) {
        // 构建从根到目标文件夹的完整路径栈
        const newPathStack: Array<{
          path: string,
          folderId: string,
          folderName: string
        }> = [];

        // 递归查找路径，同时记录 folderId
        const findPath = (node: Doc, targetId: string, currentPathStr: string, stack: typeof newPathStack): boolean => {
          if (node.id === targetId) return true;

          if (node.subs) {
            for (const sub of node.subs) {
              if (sub.nodeType === DocTypeEnum.NODE) {
                const nextPathStr = currentPathStr === "/" ? `/${sub.name}` : `${currentPathStr}/${sub.name}`;
                // 将当前节点压入路径栈
                stack.push({
                  path: currentPathStr,
                  folderId: node.id,
                  folderName: sub.name
                });
                if (findPath(sub, targetId, nextPathStr, stack)) {
                  return true;
                }
                // 回溯
                stack.pop();
              }
            }
          }
          return false;
        };

        const found = findPath(rootDoc!, currentFolder.id, "/", newPathStack);

        if (found) {
          setPathStack(newPathStack);
          setCurrentFolderId(currentFolder.id);

          // 从路径栈最后一项获取路径，然后加上当前文件夹名
          // newPathStack 最后一项的 path 是父文件夹的路径
          const lastItem = newPathStack[newPathStack.length - 1];
          if (lastItem) {
            const basePath = lastItem.path;
            setCurrentPath(basePath === "/" ? `/${currentFolder.name}` : `${basePath}/${currentFolder.name}`);
          } else {
            // 如果路径栈为空，说明是根目录的直接子文件夹
            setCurrentPath(`/${currentFolder.name}`);
          }

          // 清空搜索关键字，回到正常模式
          setSearchKeyword("");
        }
      } else {
        // 正常导航模式
        // 保存路径信息到栈
        setPathStack([...pathStack, {
          path: currentPath,
          folderId: currentFolderId,
          folderName: currentFolder.name
        }]);

        // 设置当前文件夹ID
        setCurrentFolderId(currentFolder.id);

        // 更新路径
        setCurrentPath(currentPath === "/" ? `/${currentFolder.name}` : `${currentPath}/${currentFolder.name}`);
      }
    } finally {
      // 延迟解除导航锁，防抖300ms
      setTimeout(() => {
        setIsNavigating(false);
      }, 300);
    }
  };

  const handleGoBack = () => {
    if (pathStack.length === 0) return;

    // 获取栈顶的状态（上一级的状态）
    const previousState = pathStack[pathStack.length - 1];

    // 弹出栈顶
    setPathStack(pathStack.slice(0, -1));

    // 恢复上一级的状态
    setCurrentPath(previousState.path);
    setCurrentFolderId(previousState.folderId);
  };

  const handleCreateFolder = async () => {
    if (!folderName.trim()) {
      notification.warning({ placement: 'top', message: t('components.folderNamePlaceholder') });
      return;
    }

    setIsCreatingFolder(true);
    try {
      if (!docId) {
        // 先创建根节点
        const docRes = await docAdd({
          name: dayjs().unix().toString(),
          nodeType: DocTypeEnum.NODE,
        });
        setDocId(docRes.id);
        handleNewDocId && handleNewDocId(docRes.id);

        // 设置 rootDoc，使用新创建的根节点
        const rootDocWithFolder = {
          ...docRes,
          subs: []
        };
        setRootDoc(rootDocWithFolder);
        setCurrentFolderId(docRes.id);

        // 在根节点下创建文件夹
        await createFolderInDoc(docRes.id, rootDocWithFolder);
      } else {
        await createFolderInDoc(docId, rootDoc);
      }
    } catch (error) {
      notification.error({ placement: 'top', message: t('common.addFail') });
    } finally {
      setIsCreatingFolder(false);
    }
  };

  const createFolderInDoc = async (parentDocId: string, currentRootDoc?: Doc | null) => {
    try {
      // 使用当前文件夹ID作为父级ID
      const actualParentId = currentFolderId || parentDocId;

      const folderDoc = await docAdd({
        pId: actualParentId,
        name: folderName,
        nodeType: DocTypeEnum.NODE,
      });

      // 更新 rootDoc（添加到对应文件夹的 subs）
      const docToUpdate = currentRootDoc || rootDoc;
      if (docToUpdate) {
        const updateDocTree = (doc: Doc): Doc => {
          if (doc.id === actualParentId) {
            return {
              ...doc,
              subs: [...(doc.subs || []), { ...folderDoc, creator: currentUser }]
            };
          }
          if (doc.subs) {
            return {
              ...doc,
              subs: doc.subs.map(updateDocTree)
            };
          }
          return doc;
        };
        setRootDoc(updateDocTree(docToUpdate));
      }

      setFolderModalVisible(false);
      setFolderName("");
      notification.success({ placement: 'top', message: t('common.addSuccess') });
    } catch (error) {
      throw error;
    }
  };

  // ========== 拖拽相关函数 ==========

  // 检查 targetId 是否是 folderId 的子文件夹（防止将文件夹拖入其自身或子文件夹）
  const isDescendant = (folderId: string, targetId: string): boolean => {
    if (!rootDoc) return false;

    // 先找到 folderId 对应的文件夹节点
    const findFolder = (node: Doc): Doc | null => {
      if (node.id === folderId) return node;
      if (node.subs) {
        for (const sub of node.subs) {
          const found = findFolder(sub);
          if (found) return found;
        }
      }
      return null;
    };

    const folder = findFolder(rootDoc);
    if (!folder) return false;

    // 检查 targetId 是否在 folder 的子树中
    const checkDescendant = (node: Doc): boolean => {
      if (node.id === targetId) return true;
      if (node.subs) {
        for (const sub of node.subs) {
          if (checkDescendant(sub)) return true;
        }
      }
      return false;
    };

    return checkDescendant(folder);
  };

  // 移动文档后更新状态
  const moveDocInState = (docId: string, targetFolderId: string, updatedDoc: Doc) => {
    if (!rootDoc) return;

    // 1. 先从 rootDoc 中找到要移动的文档（保留完整的 subs 数据）
    const findDoc = (node: Doc): Doc | null => {
      if (node.id === docId) return node;
      if (node.subs) {
        for (const sub of node.subs) {
          const found = findDoc(sub);
          if (found) return found;
        }
      }
      return null;
    };

    const docToMove = findDoc(rootDoc);
    if (!docToMove) return;

    // 2. 构建移动后的文档（保留原有的 subs，只更新 API 返回的字段）
    const movedDoc: Doc = {
      ...docToMove,
      ...updatedDoc,
      subs: docToMove.subs, // 保留原有的子节点
    };

    // 3. 从 rootDoc 中移除，然后添加到目标位置
    const moveDocInTree = (doc: Doc): Doc => {
      // 先移除（从原位置移除）
      const removeDoc = (node: Doc): Doc => {
        if (node.subs?.some(sub => sub.id === docId)) {
          return {
            ...node,
            subs: node.subs.filter(sub => sub.id !== docId)
          };
        }
        if (node.subs) {
          return {
            ...node,
            subs: node.subs.map(removeDoc)
          };
        }
        return node;
      };

      // 再添加到目标位置
      const addToTarget = (node: Doc): Doc => {
        if (node.id === targetFolderId) {
          return {
            ...node,
            subs: [...(node.subs || []), movedDoc]
          };
        }
        if (node.subs) {
          return {
            ...node,
            subs: node.subs.map(addToTarget)
          };
        }
        return node;
      };

      return addToTarget(removeDoc(doc));
    };

    const newRootDoc = moveDocInTree(rootDoc);
    setRootDoc(newRootDoc);
    // fileList 会通过 useEffect 根据 rootDoc 和 currentFolderId 自动更新
  };

  // 移动文档的主函数
  const handleMoveToFolder = async (dragDocId: string, targetFolderId: string, autoGoBack = false) => {
    // 校验：不能拖到自身
    if (dragDocId === targetFolderId) {
      setDropTargetId(null);
      setDropOnBreadcrumb(false);
      return;
    }

    // 校验：不能拖到自己的子文件夹（防止循环引用）
    if (isDescendant(dragDocId, targetFolderId)) {
      notification.warning({ placement: 'top', message: t('components.cannotMoveToChild') });
      setDropTargetId(null);
      setDropOnBreadcrumb(false);
      return;
    }

    setIsMoving(true);
    try {
      // 调用 API 更新 pid
      const updatedDoc = await updatePid(dragDocId, targetFolderId);

      // 更新本地状态
      moveDocInState(dragDocId, targetFolderId, updatedDoc);

      // 如果是拖到目录索引区，移动成功后自动返回上一级
      if (autoGoBack && pathStack.length > 0) {
        handleGoBack();
      }

      notification.success({ placement: 'top', message: t('components.moveSuccess') });
    } catch (error) {
      console.error('Move failed:', error);
      notification.error({ placement: 'top', message: t('components.moveFail') });
    } finally {
      setIsMoving(false);
      setDraggingId(null);
      setDropTargetId(null);
      setDropOnBreadcrumb(false);
    }
  };

  // 拖拽开始
  const handleDragStart = (e: React.DragEvent, docId: string) => {
    if (isRead || isMoving) {
      e.preventDefault();
      return;
    }
    setDraggingId(docId);
    e.dataTransfer.effectAllowed = 'move';
  };

  // 拖拽经过文件夹
  const handleDragOverFolder = (e: React.DragEvent, targetId: string) => {
    if (isRead || isMoving || !draggingId) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    e.stopPropagation();

    // 只有文件夹才能作为放置目标
    const target = fileList.find(item => item.response?.id === targetId);
    if (target?.response?.nodeType === DocTypeEnum.NODE) {
      setDropTargetId(targetId);
    }
  };

  // 拖拽离开文件夹
  const handleDragLeaveFolder = (e: React.DragEvent) => {
    e.stopPropagation();
    setDropTargetId(null);
  };

  // 放置到文件夹上
  const handleDropOnFolder = (e: React.DragEvent, targetFolderId: string) => {
    e.preventDefault();
    e.stopPropagation();

    if (draggingId) {
      handleMoveToFolder(draggingId, targetFolderId);
    }
    setDropTargetId(null);
  };

  // 拖拽经过目录索引区
  const handleDragOverBreadcrumb = (e: React.DragEvent) => {
    if (isRead || isMoving || !draggingId || pathStack.length === 0) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDropOnBreadcrumb(true);
  };

  // 拖拽离开目录索引区
  const handleDragLeaveBreadcrumb = () => {
    setDropOnBreadcrumb(false);
  };

  // 放置到目录索引区（移动到上一级）
  const handleDropOnBreadcrumb = (e: React.DragEvent) => {
    e.preventDefault();

    if (pathStack.length === 0 || !draggingId) {
      setDropOnBreadcrumb(false);
      return;
    }

    // 获取上一级的 folderId（目标文件夹）
    const previousState = pathStack[pathStack.length - 1];
    const targetFolderId = previousState.folderId;

    // 执行移动，成功后自动返回上一级
    handleMoveToFolder(draggingId, targetFolderId, true);
    setDropOnBreadcrumb(false);
  };

  return (
    <Fragment>
      {loading ? <Flex style={{ width: '100%', height: '100%' }} justify='center' align='center'><Spin /></Flex> : (
        <Fragment>
          <DocWrap accept={accept ? accept : undefined} {...props}>
            <div style={{ marginTop: 4 }}>
              <Space>
                <Button
                  icon={<FolderAddOutlined />}
                  disabled={isRead}
                  onClick={(e) => {
                    e.stopPropagation();
                    setFolderModalVisible(true);
                  }}
                >
                  {t('components.newFolder')}
                </Button>
                <Button type='primary' icon={<UploadOutlined />} disabled={isRead} >
                  {t('components.upload')}
                </Button>
                <Button type='dashed' icon={<CameraOutlined />} disabled={isRead}
                  onClick={(e) => {
                    e.stopPropagation();
                    setCameraOpen(true);
                  }}
                >
                  {t('components.cameraUpload')}
                </Button>
                <div onClick={e => e.stopPropagation()}>
                  <Input.Search
                    placeholder={t('components.searchPlaceholder')}
                    allowClear
                    value={searchKeyword}
                    onChange={(e) => setSearchKeyword(e.target.value)}
                    onSearch={(value) => setSearchKeyword(value)}
                  />
                </div>
              </Space>
              {/* 目录索引区 - 支持拖拽到上一级 */}
              <div
                className={`breadcrumb-area ${dropOnBreadcrumb ? 'drop-target' : ''}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '4px 8px 0px',
                  marginBottom: -8,
                  borderRadius: '4px',
                  border: dropOnBreadcrumb ? '1px dashed #1890ff' : '1px solid transparent',
                  backgroundColor: dropOnBreadcrumb ? '#e6f7ff' : 'transparent',
                  transition: 'all 0.2s',
                  cursor: pathStack.length > 0 ? 'pointer' : 'default'
                }}
                onClick={e => e.stopPropagation()}
                onDragOver={handleDragOverBreadcrumb}
                onDragLeave={handleDragLeaveBreadcrumb}
                onDrop={handleDropOnBreadcrumb}
              >
                {pathStack.length > 0 && (
                  <Button
                    type="text"
                    icon={<ArrowLeftOutlined />}
                    size="small"
                    onClick={handleGoBack}
                    disabled={isUploading || isMoving}
                    style={{ marginRight: 8 }}
                  />
                )}
                <Typography.Text type="secondary">
                  {currentPath}
                </Typography.Text>
                {dropOnBreadcrumb && (
                  <Typography.Text type="secondary" style={{ marginLeft: 8, color: '#1890ff' }}>
                    ({t('components.moveToParent')})
                  </Typography.Text>
                )}
              </div>
            </div>
            {!!!fileList.length && (
              <Flex style={{ width: '100%', marginTop: 12 }} justify='center' onClick={e => e.stopPropagation()}><Empty description={t('common.noFiles')} /></Flex>
            )}
          </DocWrap>
          <Modal
            title={t('components.newFolder')}
            open={folderModalVisible}
            onOk={handleCreateFolder}
            onCancel={() => {
              if (!isCreatingFolder) {
                setFolderModalVisible(false);
                setFolderName("");
              }
            }}
            confirmLoading={isCreatingFolder}
          >
            <Input
              placeholder={t('components.folderNamePlaceholder')}
              value={folderName}
              onChange={(e) => setFolderName(e.target.value)}
              onPressEnter={handleCreateFolder}
              disabled={isCreatingFolder}
            />
          </Modal>

          <MoveModal
            visible={moveModalVisible}
            onClose={() => {
              setMoveModalVisible(false);
              setSelectedDoc(null);
            }}
            selectedDoc={selectedDoc}
            rootDoc={rootDoc}
            onMove={async (targetFolderId) => {
              if (selectedDoc) {
                await handleMoveToFolder(selectedDoc.id, targetFolderId);
              }
            }}
          />

          <TakePhoto
            open={cameraOpen}
            onClose={() => setCameraOpen(false)}
            onCapture={async (file) => {
              if (!docId) {
                try {
                  const docRes = await docAdd({
                    name: dayjs().unix().toString(),
                    nodeType: DocTypeEnum.NODE,
                  });
                  setDocId(docRes.id);
                  handleNewDocId && handleNewDocId(docRes.id);
                  uploadDoc(docRes.id, file);
                } catch (e) { }
              } else {
                uploadDoc(docId, file);
              }
            }}
          />
        </Fragment>
      )}
    </Fragment>
  );
};

export default SDoc;
